import Chart from "react-apexcharts";
import { customerMaturityLine } from "../../../services/api-services";
import { useEffect, useState } from "react";
import { toastEmitter } from "../../../utills/utilities";
import { API_RESPONSE } from "../../../utills/app-constant";
import generateChartImage from "./generateChartImage";
import Remark from "./Remark";
import DownloadGraphButton from "../../../user/pages/page/DownloadGraphButton";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const CustomerMaturityLine = ({ selectedCompanyId, graphName, myhandleExport }) => {
  const [responseBody, setResponseBody] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState(0); // ✅ FIXED HERE

  const getDimenstionCurrentData = async () => {
    setLoading(true);
    try {
      const response = await customerMaturityLine(selectedCompanyId, graphName);
      if (response?.data?.responseCode === 200) {
        setResponseBody(response?.data?.responseBody || []);
        setRemark(response?.data?.remark || "");
        setRemarkId(response?.data?.remarkId || null);
        setIsAttempt(response?.data?.isAttempt ?? 0);
      } else if (response?.data?.responseCode === 404) {
        setResponseBody([]);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", API_RESPONSE.MESSAGE_503);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      getDimenstionCurrentData();
    }
  }, [selectedCompanyId]);

  const exportSummaryToExcel = (data, fileName = "Maturity Line") => {
    if (!data || !Array.isArray(data)) return;

    const heading = ["Maturity Line"];
    const allRows = [heading, []];

    data.forEach(dimension => {
      allRows.push([`Dimension: ${dimension.dimensionName}`]);
      allRows.push(["Sub-Dimension", "Gap", "Current Value", "Future Value", "Importance Value"]);

      let totalCurrent = 0;
      let totalFuture = 0;
      let totalImportance = 0;

      dimension.subDimensionMaturityLineChartResponseList.forEach(sub => {
        allRows.push([
          sub.subDimensionName,
          sub.gap,
          sub.currentValue,
          sub.futureValue,
          sub.importanceValue,
        ]);
        totalCurrent += sub.currentValue || 0;
        totalFuture += sub.futureValue || 0;
        totalImportance += sub.importanceValue || 0;
      });

      allRows.push([]);
      allRows.push([`Total Current = ${totalCurrent.toFixed(2)}`]);
      allRows.push([`Total Future = ${totalFuture.toFixed(2)}`]);
      allRows.push([`Total Importance = ${totalImportance.toFixed(2)}`]);
      allRows.push([]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(allRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };

  const renderChart = (dimensionData) => {
    const validSubDimensions = dimensionData?.subDimensionMaturityLineChartResponseList?.filter(
      (subDimension) =>
        subDimension.currentValue > 0 &&
        subDimension.futureValue > 0 &&
        subDimension.importanceValue > 0
    );

    if (validSubDimensions.length === 0) return null;

    const series = [
      {
        name: "Maturity Line",
        data: validSubDimensions.map((subDimension) => ({
          x: subDimension.subDimensionName,
          y: [
            parseFloat(subDimension.currentValue.toFixed(2)),
            parseFloat(subDimension.futureValue.toFixed(2)),
          ],
          importanceScore: subDimension.importanceValue?.toFixed(2),
        })),
      },
    ];

    const options = {
      chart: {
        height: 350,
        id: `maturity_line_${dimensionData?.dimensionName.replace(/\s+/g, "_")}`,
        type: "rangeBar",
        events: {
          mounted: () => {
            setTimeout(() => {
              generateChartImage(
                `maturity_line_${dimensionData?.dimensionName.replace(/\s+/g, "_")}`,
                "maturity_line",
                remark
              );
            }, 500);
          },
        },
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          rangeBarGroupRows: true,
          barHeight: "25%",
          borderColor: "#e0131d",
        },
      },
      grid: { show: false },
      xaxis: {
        labels: {
          show: true,
          style: { fontSize: "12px", colors: "#333" },
        },
        min: 1,
        max: 10,
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          show: true,
          style: { fontSize: "14px", colors: "#333" },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, { dataPointIndex, w }) => {
          const gap = (w.config.series[0].data[dataPointIndex].y[1] - w.config.series[0].data[dataPointIndex].y[0]).toFixed(2);
          return `Gap: ${gap}`;
        },
        style: {
          colors: ["#fff"],
          fontSize: "11px",
          fontWeight: "bold",
        },
      },
      annotations: {
        points: series[0].data.flatMap((dataPoint) => [
          {
            x: dataPoint.y[0],
            y: dataPoint.x,
            marker: { size: 0 },
            customSVG: {
              SVG: `<rect x="-3" y="-33" width="6" height="50" fill="#507e59" />`,
              offsetX: 0,
            },
          },
          {
            x: dataPoint.y[1],
            y: dataPoint.x,
            marker: { size: 0 },
            customSVG: {
              SVG: `<rect x="-3" y="-33" width="6" height="50" fill="#fdbf2d" />`,
            },
          },
          {
            x: dataPoint.importanceScore,
            y: dataPoint.x,
            marker: {
              size: 12,
              fillColor: "#e0131d",
              shape: "star",
              strokeWidth: 0,
            },
            label: {
              text: ` ${dataPoint?.importanceScore}`,
              style: { fontSize: "10px", color: "#e0131d" },
            },
          },
        ]),
      },
      tooltip: {
        enabled: true,
        custom: ({ dataPointIndex, w }) => {
          const data = w.config.series[0].data[dataPointIndex];
          const gap = (data.y[1] - data.y[0]).toFixed(2);
          return `
            <div style="padding: 8px; font-size: 12px;">
              <strong>${data.x}</strong><br />
              <span>Current: ${data.y[0]}</span><br/>
              <span>Future: ${data.y[1]}</span><br/>
              <span>Gap: ${gap}</span><br/>
              <span>Importance: ${data.importanceScore}</span>
            </div>`;
        },
      },
    };

    return (
      <div key={dimensionData.id} id="chart" className={`chart16-${dimensionData.id}`} data-heading="Maturity Line" data-remark={remark}>
        <div className="d-flex justify-content-between">
          <h5 style={{ marginBottom: "20px" }}>{dimensionData.dimensionName}</h5>
          <div className="d-flex">
            <DownloadGraphButton chartID={`chart16-${dimensionData.id}`} myhandleExport={myhandleExport} />
            <button className="mb-3 dbtn px-3 py-2 text-white bg-primary rounded border-0 cursor-pointer me-3" onClick={() => exportSummaryToExcel(responseBody)}>
              Download Excel
            </button>
          </div>
        </div>
        <div style={{ marginLeft: "30px", marginTop: "20px", display: "flex", gap: "50px" }}>
          <Legend color="#29cc27" label="Current Score" />
          <Legend color="#fdbf2d" label="Future Score" />
          <Legend shape="star" color="#e0131d" label="Importance Score" />
          <Legend color="#5ccafd" label="Gap" small />
        </div>
        {loading ? (
          <div className="loader-container"><div className="loader"></div></div>
        ) : (
          <Chart options={options} series={series} type="rangeBar" height={400} />
        )}
      </div>
    );
  };

  return (
    <div>
      {loading ? (
        <div className="loader-container"><div className="loader"></div></div>
      ) : responseBody && responseBody.length > 0 ? (
        responseBody.map((dimensionData) => renderChart(dimensionData))
      ) : (
        <p>No data available to display.</p>
      )}

      <Remark
        selectedCompanyId={selectedCompanyId}
        graphName="CustomerMaturityLine"
        initialRemark={remark}
        remarkId={remarkId}
        isAttempt={isAttempt}
        setRemarkprops={setRemark}
      />
    </div>
  );
};

const Legend = ({ color, label, shape, small }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
    <span
      style={{
        width: small ? "15px" : shape === "star" ? "20px" : "4px",
        height: small ? "15px" : shape === "star" ? "20px" : "20px",
        backgroundColor: color,
        display: "inline-block",
        ...(shape === "star"
          ? {
            clipPath:
              "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
          }
          : {}),
      }}
    ></span>
    <span style={{ fontSize: "14px", color: "#333" }}>{label}</span>
  </div>
);

export default CustomerMaturityLine;
